import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Tooltip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Menu,
  MenuItem,
  Avatar,
  useTheme,
} from '@mui/material';
import {
  History,
  Download,
  Restore,
  MoreVert,
  Description,
  Edit,
  CloudUpload,
  ChangeCircle,
  Check,
  Close,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { getAuthToken } from '../../lib/authToken';

interface DocumentVersion {
  id: string;
  versionNumber: number;
  versionLabel: string;
  fileName: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  changeType: string;
  changeDescription: string;
  isCurrent: boolean;
  checksum: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  } | null;
  createdAt: string;
}

interface VersionHistoryData {
  documentId: string;
  documentTitle: string;
  currentVersion: number;
  totalVersions: number;
  versions: DocumentVersion[];
}

interface DocumentVersionHistoryProps {
  documentId: string;
  documentTitle: string;
  open: boolean;
  onClose: () => void;
  onVersionRestored?: () => void;
}

// Fetch version history
const fetchVersionHistory = async (documentId: string): Promise<{ data: VersionHistoryData }> => {
  const token = getAuthToken();
  const response = await fetch(`/api/documents/${documentId}/versions`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch version history');
  return response.json();
};

// Download a specific version
const downloadVersion = async (documentId: string, versionId: string, fileName: string) => {
  const token = getAuthToken();
  const response = await fetch(`/api/documents/${documentId}/versions/${versionId}/download`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
  
  if (!response.ok) throw new Error('Failed to download version');
  
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Restore a version
const restoreVersion = async ({ documentId, versionId }: { documentId: string; versionId: string }) => {
  const token = getAuthToken();
  const response = await fetch(`/api/documents/${documentId}/versions/${versionId}/restore`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) throw new Error('Failed to restore version');
  return response.json();
};

// Update version label
const updateVersionLabel = async ({ 
  documentId, 
  versionId, 
  versionLabel, 
  changeDescription 
}: { 
  documentId: string; 
  versionId: string; 
  versionLabel?: string;
  changeDescription?: string;
}) => {
  const token = getAuthToken();
  const response = await fetch(`/api/documents/${documentId}/versions/${versionId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ versionLabel, changeDescription }),
  });
  
  if (!response.ok) throw new Error('Failed to update version');
  return response.json();
};

const DocumentVersionHistory: React.FC<DocumentVersionHistoryProps> = ({
  documentId,
  documentTitle,
  open,
  onClose,
  onVersionRestored,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [menuAnchorEl, setMenuAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
  const [editingVersion, setEditingVersion] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [confirmRestore, setConfirmRestore] = useState<DocumentVersion | null>(null);

  // Fetch version history
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['document-versions', documentId],
    queryFn: () => fetchVersionHistory(documentId),
    enabled: open && !!documentId,
  });

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: restoreVersion,
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['document-versions', documentId] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setConfirmRestore(null);
      onVersionRestored?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update label mutation
  const updateLabelMutation = useMutation({
    mutationFn: updateVersionLabel,
    onSuccess: () => {
      toast.success('Version label updated');
      queryClient.invalidateQueries({ queryKey: ['document-versions', documentId] });
      setEditingVersion(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const versions = data?.data?.versions || [];

  const handleMenuOpen = (versionId: string, event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl({ ...menuAnchorEl, [versionId]: event.currentTarget });
  };

  const handleMenuClose = (versionId: string) => {
    setMenuAnchorEl({ ...menuAnchorEl, [versionId]: null });
  };

  const handleDownload = async (version: DocumentVersion) => {
    try {
      await downloadVersion(documentId, version.id, version.originalName);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download version');
    }
  };

  const handleStartEdit = (version: DocumentVersion) => {
    setEditingVersion(version.id);
    setEditLabel(version.versionLabel);
    handleMenuClose(version.id);
  };

  const handleSaveLabel = (version: DocumentVersion) => {
    updateLabelMutation.mutate({
      documentId,
      versionId: version.id,
      versionLabel: editLabel,
    });
  };

  const getChangeTypeIcon = (changeType: string) => {
    switch (changeType) {
      case 'upload':
        return <CloudUpload fontSize="small" />;
      case 'update':
      case 'minor_edit':
        return <Edit fontSize="small" />;
      case 'major_revision':
        return <ChangeCircle fontSize="small" />;
      case 'restore':
      case 'rollback':
        return <Restore fontSize="small" />;
      default:
        return <Description fontSize="small" />;
    }
  };

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'upload':
        return 'primary';
      case 'major_revision':
        return 'warning';
      case 'restore':
      case 'rollback':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History color="primary" />
            <Typography variant="h6">Version History</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {documentTitle}
          </Typography>
        </DialogTitle>
        
        <DialogContent dividers>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">
              Failed to load version history. Please try again.
            </Alert>
          ) : versions.length === 0 ? (
            <Alert severity="info">
              No version history available for this document.
            </Alert>
          ) : (
            <Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                <Chip 
                  label={`${versions.length} version${versions.length !== 1 ? 's' : ''}`} 
                  size="small" 
                  color="primary" 
                />
                <Chip 
                  label={`Current: v${data?.data?.currentVersion}`} 
                  size="small" 
                  color="success" 
                />
              </Box>
              
              <List>
                {versions.map((version, index) => (
                  <React.Fragment key={version.id}>
                    <ListItem
                      sx={{
                        bgcolor: version.isCurrent ? 'action.selected' : 'transparent',
                        borderRadius: 1,
                        mb: 1,
                        border: version.isCurrent ? `2px solid ${theme.palette.success.main}` : '1px solid',
                        borderColor: version.isCurrent ? 'success.main' : 'divider',
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: version.isCurrent ? 'success.main' : 'grey.400',
                            width: 40,
                            height: 40,
                          }}
                        >
                          {version.isCurrent ? <Check /> : getChangeTypeIcon(version.changeType)}
                        </Avatar>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {editingVersion === version.id ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                  size="small"
                                  value={editLabel}
                                  onChange={(e) => setEditLabel(e.target.value)}
                                  autoFocus
                                />
                                <IconButton size="small" onClick={() => handleSaveLabel(version)}>
                                  <Check fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => setEditingVersion(null)}>
                                  <Close fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <>
                                <Typography variant="subtitle2">
                                  v{version.versionNumber} - {version.versionLabel}
                                </Typography>
                                {version.isCurrent && (
                                  <Chip label="Current" size="small" color="success" />
                                )}
                                <Chip 
                                  label={version.changeType.replace('_', ' ')} 
                                  size="small" 
                                  color={getChangeTypeColor(version.changeType) as any}
                                  variant="outlined"
                                />
                              </>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {version.changeDescription || 'No description'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {version.createdBy?.name || 'Unknown'} • {formatDistanceToNow(new Date(version.createdAt), { addSuffix: true })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatFileSize(version.fileSize)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Tooltip title="Download">
                          <IconButton onClick={() => handleDownload(version)}>
                            <Download />
                          </IconButton>
                        </Tooltip>
                        <IconButton onClick={(e) => handleMenuOpen(version.id, e)}>
                          <MoreVert />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchorEl[version.id]}
                          open={Boolean(menuAnchorEl[version.id])}
                          onClose={() => handleMenuClose(version.id)}
                        >
                          <MenuItem onClick={() => handleDownload(version)}>
                            <Download fontSize="small" sx={{ mr: 1 }} /> Download
                          </MenuItem>
                          <MenuItem onClick={() => handleStartEdit(version)}>
                            <Edit fontSize="small" sx={{ mr: 1 }} /> Edit Label
                          </MenuItem>
                          {!version.isCurrent && (
                            <MenuItem 
                              onClick={() => { 
                                handleMenuClose(version.id);
                                setConfirmRestore(version);
                              }}
                            >
                              <Restore fontSize="small" sx={{ mr: 1 }} /> Restore This Version
                            </MenuItem>
                          )}
                        </Menu>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < versions.length - 1 && (
                      <Box sx={{ ml: 5, borderLeft: '2px dashed', borderColor: 'divider', height: 20 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Restore Dialog */}
      <Dialog open={!!confirmRestore} onClose={() => setConfirmRestore(null)}>
        <DialogTitle>Restore Version?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to restore <strong>v{confirmRestore?.versionNumber} - {confirmRestore?.versionLabel}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will create a new version based on the selected version. The current version will be preserved in the history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestore(null)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              if (confirmRestore) {
                restoreMutation.mutate({
                  documentId,
                  versionId: confirmRestore.id,
                });
              }
            }}
            disabled={restoreMutation.isPending}
          >
            {restoreMutation.isPending ? 'Restoring...' : 'Restore'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentVersionHistory;
